import React, { memo, useContext } from 'react';
import { AppContext } from 'src/components/app-context';
import { useProductContext } from '../product-context';

import { Currency, formatCurrency } from 'src/components/currency';
import Graph from 'src/areas/product/components/graph';
import PriceHistoryCurve from 'src/areas/product/components/graph/components/price-history-curve';

import { formatDate } from '../product-price-history-legacy/utils';

import type { ProductStatistics } from 'src/types';
import type {
  IGraphItem,
  IGraphItemRenderProps,
} from 'src/areas/product/components/graph/types';

import {
  StyledCurrentPrice,
  StyledFadingBox,
  StyledPriceHistoryHeading,
  StyledPriceHistorySummary,
  StyledPriceHistoryWrapper,
} from './styles';

export const PriceHistorySummary: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IPriceHistorySummaryProps>>
> = memo(props => {
  const { isLoading } = props;
  const { t, market } = useContext(AppContext);
  const { product } = useProductContext();

  if (isLoading || !product.statistics?.nodes) {
    return <StyledPriceHistorySummary />;
  }

  const lowestPriceNodes = product.statistics.nodes.filter(isFromLastSixMonths);

  const graphs: IGraphItem<IGraphItemRenderProps>[] = [
    {
      name: 'price-history',
      legend: {
        label: t('product', 'price-history.actual-price'),
        color: '#4895B6',
        lineType: 'normal',
      },
      render: (graphProps: IGraphItemRenderProps) => (
        <PriceHistoryCurve
          key={graphProps.name}
          data={lowestPriceNodes}
          {...graphProps}
        />
      ),
    },
  ];

  const xScaleData = lowestPriceNodes
    .filter(p => p.date)
    .map(({ date }) => new Date(date!));
  const yScaleData = lowestPriceNodes
    .filter(p => p.lowestPrice)
    .map(({ lowestPrice }) => lowestPrice!);

  return (
    <StyledPriceHistorySummary>
      <StyledFadingBox>
        <StyledPriceHistoryHeading>
          {t('product', 'price-history.title')}
        </StyledPriceHistoryHeading>
        <StyledCurrentPrice>
          {t('product', 'price-history.current-lowest-price', {
            lowestPrice: (
              <Currency
                key="lowestPrice"
                price={
                  product.priceSummary.regular ||
                  product.priceSummary.alternative
                }
              />
            ),
          })}
        </StyledCurrentPrice>
        <StyledPriceHistoryWrapper>
          {IS_BROWSER && (
            <Graph
              width={310}
              aspectRatio={2.5}
              showXGrid={false}
              showYGrid={false}
              showXAxis={false}
              showYAxis={false}
              showLegend={false}
              xTickFormat={formatDate}
              yTickFormat={(value: number) =>
                formatCurrency(value, market.code, false, market.currency!) ||
                value.toString()
              }
              renderTooltip={tooltip => (
                <>
                  <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                    {tooltip.xValue}
                  </div>
                  <div>
                    <Currency price={tooltip.yValue} market={market.code} />
                  </div>
                </>
              )}
              xScaleData={xScaleData}
              yScaleData={yScaleData}
              graphs={graphs}
              nodes={[
                product.statistics.nodes
                  .filter(Boolean)
                  .filter(isFromLastSixMonths)
                  .map(item => ({
                    date: item?.date,
                    value: item?.lowestPrice,
                  })),
              ]}
            />
          )}
        </StyledPriceHistoryWrapper>
      </StyledFadingBox>
    </StyledPriceHistorySummary>
  );
});

const sixMonthsAgo = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return date.valueOf();
})();

const isFromLastSixMonths = <T extends Pick<ProductStatistics, 'date'>>(
  node: T
): boolean => {
  if (!node?.date) {
    return false;
  }
  const [year, month, day] = node.date.split('-');
  return (
    new Date(Number(year), Number(month) - 1, Number(day)).valueOf() >
    sixMonthsAgo
  );
};

interface IPriceHistorySummaryProps {
  isLoading: boolean;
}
